import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Comment, Discussion } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { posLabel, type PosCtx } from "../lib/format";
import { Avatar } from "./ui";
import { ProfileFace } from "./Profile";
import { AskIcon, CheckIcon, FlagIcon, LockIcon, MoreIcon, LampIcon } from "./Icons";
import { talk, when, dayOf, dayLabel, vantage } from "@pagemates/shared/copy";

const txt = talk.discussion;

const URL_SPLIT = /(https?:\/\/[^\s<>"')\]]+)/g;
const IS_URL = /^https?:\/\//;

/** Auto-links URLs and highlights @mentions of people in the discussion. */
export function renderBody(text: string, names: string[]): ReactNode[] {
  const esc = names.filter(Boolean).sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const AT = esc.length ? new RegExp(`(@(?:${esc.join("|")}))(?![\\w])`, "g") : null;
  return text.split(URL_SPLIT).flatMap((part, i) => {
    if (IS_URL.test(part)) return [<a key={`u${i}`} href={part} target="_blank" rel="noopener noreferrer">{part}</a>];
    if (!AT) return [part];
    return part.split(AT).map((p, j) => (p.startsWith("@") && esc.length && new RegExp(`^@(?:${esc.join("|")})$`).test(p) ? <span key={`a${i}-${j}`} className="at">{p}</span> : p));
  });
}

// Times and where-from in words are shared with the app (packages/shared/src/copy).
export { when, dayOf, dayLabel, vantage };

/** The sealed strip: a face, the lamp's hatching and a lock. SealedRow fills it for Talk; the home page's demo uses it bare. */
export function SealedStrip({ face, className, children }: { face: ReactNode; className?: string; children: ReactNode }) {
  return (
    <div className={`sealed${className ? ` ${className}` : ""}`}>
      {face}
      <div className="row-s"><LockIcon />{children}</div>
    </div>
  );
}

export function SealedRow({ c, inThread, answer, ctx, onReveal }:
  { c: Comment; inThread?: boolean; answer?: boolean; ctx: PosCtx; onReveal?: () => void }) {
  return (
    <SealedStrip className={c.kind === "finishline" ? "finishline" : undefined}
      face={c.kind === "finishline" ? <span className="avatar fl-lamp" aria-hidden="true"><LampIcon width={18} height={18} /></span> : <Avatar name={c.author.name} image={c.author.image} />}>
      {c.kind === "finishline" ? <span><b>{txt.finishLine}</b>{txt.forEveryoneWhoHas(!inThread && c.replyCount > 0 ? ` · ${c.replyCount} ${c.replyCount === 1 ? txt.reply : txt.replies}` : "")}</span>
      : answer ? <span><b>{txt.spoilerAnswer}</b>{txt.opensAt(c.author.name, posLabel(c.position, ctx))}</span> : (
        <span><b>{txt.until(inThread ? txt.sealed : txt.spoiler, posLabel(c.position, ctx))}</b> · {c.author.name}
          {!inThread && c.replyCount > 0 ? ` · ${c.replyCount} ${c.replyCount === 1 ? txt.reply : txt.replies}` : ""}</span>
      )}
      {onReveal && <button type="button" className="reveal" onClick={onReveal}>{txt.readItAnyway}</button>}
    </SealedStrip>
  );
}

/** A message with nothing to tap: the examples in the creation flow and on the home page. */
export function StaticMessage({ name, pos, body, className }: { name: string; pos: string; body: string; className?: string }) {
  return (
    <div className={`msg${className ? ` ${className}` : ""}`}>
      <Avatar name={name} />
      <div style={{ minWidth: 0 }}>
        <div className="who"><b>{name}</b><span className="pos">{pos}</span></div>
        <div className="body">{body}</div>
      </div>
    </div>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function Editor({ clubId, bookId, c, onDone }: { clubId: string; bookId: string; c: Comment; onDone: () => void }) {
  const qc = useQueryClient();
  const [body, setBody] = useState(c.body ?? "");
  const save = useMutation({
    mutationFn: () => api.editComment(clubId, c.id, { body }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["discussion", clubId, bookId] }); onDone(); },
  });
  return (
    <form className="stack-sm" style={{ marginTop: 4 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <textarea id={`edit-${c.id}`} className="input" value={body} maxLength={4000} onChange={(e) => setBody(e.target.value)} autoFocus />
      <div className="row">
        <button type="submit" className="btn primary sm" disabled={!body.trim() || save.isPending}>{txt.save}</button>
        <button type="button" className="btn ghost sm" onClick={onDone}>{txt.cancel}</button>
      </div>
      {save.isError && <p className="error">{describeError(save.error)}</p>}
    </form>
  );
}

/** One message, in the stream or in a thread. `showPos` hides the pill when it matches the previous message. */
export function Message({ clubId, bookId, c, d, names, isAdmin, showPos, onOpenThread, onReplyTo, isNew, arrive, inQuestion, canReveal, justOpened }:
  { clubId: string; bookId: string; c: Comment; d: Discussion; names: string[]; isAdmin: boolean; showPos: boolean;
    onOpenThread?: (id: string) => void; onReplyTo?: (c: Comment) => void; isNew: boolean; arrive?: boolean;
    /** opened by the viewer's latest unsealing moment: "Just opened", and a paper unfold the first time it scrolls into view */
    justOpened?: boolean;
    /** this is an answer under a question: show where it came from instead of a position */
    inQuestion?: boolean;
    /** the viewer asked this question and the club allows opening sealed answers early */
    canReveal?: boolean }) {
  const qc = useQueryClient();
  const [menu, setMenu] = useState(false);
  const [menuUp, setMenuUp] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const del = useMutation({ mutationFn: () => api.deleteComment(clubId, c.id), onSuccess: () => qc.invalidateQueries({ queryKey: ["discussion", clubId, bookId] }) });
  const flag = useMutation({ mutationFn: (f: boolean) => api.flagComment(clubId, c.id, f), onSuccess: () => qc.invalidateQueries({ queryKey: ["discussion", clubId, bookId] }) });
  const answered = useMutation({ mutationFn: (a: boolean) => api.answerComment(clubId, c.id, a), onSuccess: () => qc.invalidateQueries({ queryKey: ["discussion", clubId, bookId] }) });
  const reveal = useMutation({ mutationFn: () => api.revealAnswer(clubId, c.id), onSuccess: () => qc.invalidateQueries({ queryKey: ["discussion", clubId, bookId] }) });
  const [unfolded, setUnfolded] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!justOpened || unfolded || !el) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setUnfolded(true); io.disconnect(); } }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [justOpened, unfolded]);
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  if (c.locked) return (
    <div className={arrive ? "arrive" : undefined}>
      <SealedRow c={c} inThread={!onOpenThread} answer={inQuestion} ctx={d} onReveal={inQuestion && canReveal && !reveal.isPending ? () => reveal.mutate() : undefined} />
      {reveal.isError && <p className="error small">{describeError(reveal.error)}</p>}
    </div>
  );
  const participants = Array.from(new Map(c.replies.map((r) => [r.author.id, r.author])).values()).slice(0, 3);
  const isQ = c.kind === "question";
  const canAnswer = isQ && !c.mine && !c.answered && (d.myStatus !== "reading" || d.myProgress > c.position) && !c.replies.some((r) => r.mine);
  const from = inQuestion && !c.mine ? vantage(c.fromPercent) : null;
  // Your own spoiler answer: say where it opens, since it looks like any other reply to you.
  const spoilerAt = inQuestion && c.mine && showPos ? c.position : null;

  return (
    <div id={`m-${c.id}`} className={`msg${justOpened ? ` opened${unfolded ? " unfold" : ""}` : ""}${c.removed ? " gone" : ""}${onOpenThread && !c.removed ? " openable" : ""}${arrive ? " arrive" : ""}${c.kind === "finishline" ? " finishline" : ""}${c.kind === "quote" ? " is-quote" : ""}${isQ ? " is-question" : ""}`} ref={ref}>
      {c.kind === "finishline" ? <span className="avatar fl-lamp" aria-hidden="true"><LampIcon width={18} height={18} /></span> : c.removed ? <Avatar name={c.author.name} image={c.author.image} /> : <ProfileFace user={c.author} />}
      <div style={{ minWidth: 0 }}>
        <div className="who">
          <span className="who-main">{/* these wrap among themselves; the time stays on the first line */}
          {isNew && !justOpened && <span className="newdot" aria-label={txt.new} />}
          {c.kind === "finishline" ? <b className="fl-label">{txt.finishLine}</b> : <b>{c.mine ? txt.you : c.author.name}</b>}
          {c.flagged && <span className={`flagmark${c.discussed ? " done" : ""}`} title={c.discussed ? txt.discussedAtTheMeeting : txt.forTheMeeting} aria-label={c.discussed ? txt.discussedAtTheMeeting : txt.flaggedForTheMeeting}>{c.discussed ? <CheckIcon width={13} height={13} /> : <FlagIcon width={13} height={13} />}</span>}
          {c.author.former && <span className="pill">{txt.left}</span>}
          {from ? <span className="pos ahead">{from}{c.revealed ? txt.openedEarly : ""}</span> : spoilerAt !== null ? <span className="pos seal"><LockIcon width={10} height={10} />{txt.spoiler2(posLabel(spoilerAt, d))}</span>
            : showPos && !c.removed && <span className={`pos${c.mine ? " mine" : ""}`}>{posLabel(c.position, d)}</span>}
          {justOpened && <span className="just-opened">{txt.justOpened}</span>}
          </span>
          <span className="when">{when(c.createdAt)}{c.editedAt ? txt.edited : ""}</span>
        </div>
        {editing && c.body !== null ? (
          <Editor clubId={clubId} bookId={bookId} c={c} onDone={() => setEditing(false)} />
        ) : (
          <div className="body" onClick={onOpenThread && !c.removed ? () => onOpenThread(c.id) : undefined}
            role={onOpenThread && !c.removed ? "button" : undefined} tabIndex={onOpenThread && !c.removed ? 0 : undefined}
            onKeyDown={onOpenThread && !c.removed ? (e) => { if (e.key === "Enter") onOpenThread(c.id); } : undefined}>
            {c.removed ? txt.commentRemoved : c.kind === "quote" ? <blockquote className="qblock"><span className="qmark" aria-hidden="true">“</span>{renderBody(c.body ?? "", names)}</blockquote>
              : isQ ? (
                <div className={`qcard${c.answered ? " done" : ""}`}>
                  <div className="k">{c.answered ? <><CheckIcon width={14} height={14} />{txt.answered}</> : <><AskIcon width={14} height={14} />{txt.questionForPeopleAhead}</>}</div>
                  <div className="qbody">{renderBody(c.body ?? "", names)}</div>
                  {onOpenThread ? (
                    <div className="foot">
                      <span>{c.replyCount === 0 ? txt.noAnswersYet : `${plural(c.replyCount, txt.answer, txt.answers)} · ${participants.map((p) => p.name).join(", ")}`}</span>
                      {canAnswer && <span className="go">{txt.youReAheadAnswer}</span>}
                    </div>
                  ) : c.mine && !c.answered && c.replyCount > 0 ? (
                    <div className="foot"><button type="button" className="btn sm" disabled={answered.isPending} onClick={() => answered.mutate(true)}><CheckIcon width={14} height={14} />{txt.thatAnswersIt}</button></div>
                  ) : null}
                </div>
              ) : renderBody(c.body ?? "", names)}
          </div>
        )}
        {onOpenThread && !c.removed && !isQ && c.replyCount === 0 && c.kind !== "finishline" && (
          <button type="button" className="replies none" onClick={() => onOpenThread(c.id)}>{txt.reply2}</button>
        )}
        {onOpenThread && !c.removed && !isQ && c.replyCount > 0 && (
          <button type="button" className="replies" onClick={() => onOpenThread(c.id)}>
            <span className="avs">{participants.map((p) => <Avatar key={p.id} name={p.name} image={p.image} />)}</span>
            {c.replyCount} {c.replyCount === 1 ? txt.reply : txt.replies}{c.lastReplyAt ? ` · ${when(c.lastReplyAt)}` : ""}
          </button>
        )}

        {confirm && (
          <div className="row small" style={{ marginTop: 6 }}>
            <span className="muted">{txt.deleteThis(c.replyCount ? txt.repliesStay(c.replyCount) : "")}</span>
            <button className="btn danger sm" disabled={del.isPending} onClick={() => del.mutate()}>{txt.delete}</button>
            <button className="btn ghost sm" onClick={() => setConfirm(false)}>{txt.keep}</button>
          </div>
        )}
        {del.isError && <p className="error">{describeError(del.error)}</p>}
      </div>
      {!c.removed && (c.mine || isAdmin || onReplyTo || onOpenThread) && (
        <>
          <button type="button" className="more" aria-label={txt.more} aria-expanded={menu}
            onClick={(e) => {
              // Open upward when the message sits near the bottom of its scroll container.
              const scroller = (e.currentTarget.closest(".stream, .thread .list") as HTMLElement | null);
              const r = e.currentTarget.getBoundingClientRect();
              const bottom = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight;
              setMenuUp(bottom - r.bottom < 170);
              setMenu((m) => !m);
            }}><MoreIcon width={18} height={18} /></button>
          {menu && (
            <div className={`menu${menuUp ? " up" : ""}`} role="menu">
              {onOpenThread && <button role="menuitem" onClick={() => { setMenu(false); onOpenThread(c.id); }}>{isQ ? txt.openTheQuestion : txt.replyInThread}</button>}
              {isQ && c.mine && <button role="menuitem" onClick={() => { setMenu(false); answered.mutate(!c.answered); }}>{c.answered ? txt.notAnsweredYet : txt.thatAnswersIt}</button>}
              <button role="menuitem" onClick={() => { setMenu(false); flag.mutate(!c.flagged); }}>{c.flagged ? txt.notForTheMeeting : txt.forTheMeeting}</button>
              {onReplyTo && <button role="menuitem" onClick={() => { setMenu(false); onReplyTo(c); }}>{txt.replyTo(c.mine ? txt.yourself : c.author.name)}</button>}
              {c.mine && c.body !== null && c.kind !== "finishline" && <button role="menuitem" onClick={() => { setMenu(false); setEditing(true); }}>{txt.edit}</button>}
              {(c.mine || isAdmin) && c.kind !== "finishline" && <button role="menuitem" className="danger" onClick={() => { setMenu(false); setConfirm(true); }}>{txt.delete}</button>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
